import { useState, useMemo } from 'react';
import { Modal } from './Modal/Modal';
import { SegmentedToggle } from './SegmentedToggle/SegmentedToggle';
import { useUiStore } from '../store/ui';
import { useYearBundle } from '../store/queryHooks';
import { generateBudgetCsv } from '../features/import/generateBudgetCsv';
import { downloadTextFile } from '../lib/download';
import type { CategoryKind } from '@budgetx/shared';
import styles from './ExportModal.module.css';

interface Props {
  onClose(): void;
}

type ExposedKind = 'income' | 'fast' | 'variabel';

const KIND_LABEL: Record<ExposedKind, string> = {
  income: 'Indtægter',
  fast: 'Faste udgifter',
  variabel: 'Variable udgifter',
};

function filenameFor(kind: CategoryKind, year: number): string {
  switch (kind) {
    case 'income':
      return `indtaegter-${year}.csv`;
    case 'fast':
      return `faste-udgifter-${year}.csv`;
    case 'variabel':
      return `variable-udgifter-${year}.csv`;
    case 'opsparing':
      return `opsparing-${year}.csv`;
  }
}

const EXPORT_OPTIONS = [
  { value: 'income' as const, label: 'Indtægter' },
  { value: 'fast' as const, label: 'Faste' },
  { value: 'variabel' as const, label: 'Variable' },
] as const;

export function ExportModal({ onClose }: Props) {
  const year = useUiStore((s) => s.year);
  const setToast = useUiStore((s) => s.setToast);
  const bundle = useYearBundle(year);
  const [exportKind, setExportKind] = useState<ExposedKind>('fast');

  const countByKind = useMemo(() => {
    const counts: Record<ExposedKind, number> = { income: 0, fast: 0, variabel: 0 };
    for (const c of bundle.data?.categories ?? []) {
      if (c.kind !== 'opsparing') counts[c.kind]++;
    }
    return counts;
  }, [bundle.data]);

  const csvDisabled = bundle.isLoading || countByKind[exportKind] === 0;

  function doCsv() {
    if (!bundle.data) return;
    const csv = generateBudgetCsv(bundle.data, exportKind);
    const filename = filenameFor(exportKind, year);
    downloadTextFile(csv, filename, 'text/csv');
    setToast(`Eksporteret ${filename}`);
    onClose();
  }

  function doPrint() {
    onClose();
    // Give the modal time to unmount before print dialog
    window.setTimeout(() => window.print(), 120);
  }

  return (
    <Modal
      title="Eksportér budget"
      subtitle="Gem hele året i dit foretrukne format."
      onClose={onClose}
    >
      <div className={styles.kindPicker}>
        <SegmentedToggle
          options={EXPORT_OPTIONS}
          value={exportKind}
          onChange={(kind) => setExportKind(kind)}
        />
      </div>
      <div className={styles.list}>
        <button className={styles.opt} onClick={doCsv} disabled={csvDisabled} type="button">
          <span className={styles.pill}>CSV</span>
          <div className={styles.grow}>
            <div className={styles.name}>CSV-fil ({KIND_LABEL[exportKind]})</div>
            <div className={styles.sub}>Samme format som importen — kan læses tilbage</div>
          </div>
          <span className={styles.arrow}>↓</span>
        </button>
        {!bundle.isLoading && countByKind[exportKind] === 0 && (
          <div className={styles.help}>
            Ingen {KIND_LABEL[exportKind].toLowerCase()} at eksportere for {year}.
          </div>
        )}
        <button className={styles.opt} onClick={doPrint} type="button">
          <span className={styles.pill}>PDF</span>
          <div className={styles.grow}>
            <div className={styles.name}>PDF-rapport</div>
            <div className={styles.sub}>Print / gem som PDF — klar til banken</div>
          </div>
          <span className={styles.arrow}>↓</span>
        </button>
      </div>
    </Modal>
  );
}
